import { Injectable } from '@angular/core';

import * as sdk from '@mycure/sdk';
import { MycureSDK } from './../service';
import * as _ from 'lodash';

import { Logger } from './../logger/logger.service';
const log = new Logger('@mycure/mc-services');

@Injectable({
  providedIn: 'root'
})

export class McMedRecordService {

  constructor(
    private sdk: MycureSDK,
  ) {

  }

  loadRecord(id: string) {
    return this.sdk.medicalRecords().get(id);
  }

  async loadLabResultRecord(id: string) {
    const query = {
      id,
      $populate: {
        tests: {
          service: 'lis-tests', key: 'results',
          extractKey: 'test',
          $populate: {
            measures: {
              service: 'lis-measures',
              key: 'id', method: 'find',
              idField: 'test'
            }
          }
        }
      }
    }

    let res: any = await this.sdk.medicalRecords().findOne(query);
    if (!res) throw new Error('error med record');

    res = {
      ..._.omit(res, ['$populated']),
      _tests: res.$populated.tests,
    }

    // if has _test, move measures
    if (res._tests) {
      res._tests = res._tests.map((item: any) => {
        return {
          ..._.omit(item, ['$populated']),
          _measures: item.$populated.measures,
        }
      });
    }

    return res;
  }

  removeRecord(id: string) {
    return this.sdk.medicalRecords().remove(id);
  }

  /**
   * Create progress note record
   */
  createMedRecordsFromProgressNote(data: any): Promise<any> {
    let promises: Promise<any>[] = [];
    if (data) {
      if (!_.isEmpty(data.s_cc))
        promises.push(this.createChiefComplaint({ ...data, text: data.s_cc }));

      if (!_.isEmpty(data.s_hpi))
        promises.push(this.createHPI({ ...data, text: data.s_hpi }));

      if (data.o_height ||
        data.o_weight ||
        data.o_pulse ||
        data.o_respiration ||
        data.o_oxygenSaturation ||
        data.o_temperature ||
        data.o_temperatureMethod ||
        data.o_bpDiastolic ||
        data.o_bpSystolic ||
        data.o_headCircumference ||
        data.o_waistCircumference ||
        data.o_neckCircumference ||
        data.o_abdominalCircumference)
        promises.push(this.createVital({
          ...data,
          height: data.o_height,
          weight: data.o_weight,
          pulse: data.o_pulse,
          respiration: data.o_respiration,
          oxygenSaturation: data.o_oxygenSaturation,
          temperature: data.o_temperature,
          temperatureMethod: data.o_temperatureMethod,
          bpDiastolic: data.o_bpDiastolic,
          bpSystolic: data.o_bpSystolic,
          headCircumference: data.o_headCircumference,
          waistCircumference: data.o_waistCircumference,
          neckCircumference: data.o_neckCircumference,
          abdominalCircumference: data.o_abdominalCircumference
        }));

      if (data.o_cpapPressure ||
        data.o_bipapInspiration ||
        data.o_bipapExpiration ||
        data.o_lsatPressure ||
        data.o_ahi ||
        data.o_rampTime ||
        !_.isEmpty(data.o_psgDiagnostic) ||
        !_.isEmpty(data.o_psgTherapeutic) ||
        !_.isEmpty(data.o_psgSplit))
        promises.push(this.createENTNote({
          ...data,
          cpapPressure: data.o_cpapPressure,
          bipapInspiration: data.o_bipapInspiration,
          bipapExpiration: data.o_bipapExpiration,
          lsatPressure: data.o_lsatPressure,
          ahi: data.o_ahi,
          rampTime: data.o_rampTime,
          psgDiagnostic: data.o_psgDiagnostic,
          psgTherapeutic: data.o_psgTherapeutic,
          psgSplit: data.o_psgSplit
        }));

      if (data.o_lmp ||
        data.o_fundalHeight ||
        data.o_fetalHeartTone ||
        !_.isEmpty(data.o_examFindings) ||
        data.o_ultrasoundDate ||
        data.o_ultrasoundAog)
        promises.push(this.createOBNote({
          ...data,
          lmp: data.o_lmp ? new Date(data.o_lmp) : undefined,
          fundalHeight: data.o_fundalHeight,
          fetalHeartTone: data.o_fetalHeartTone,
          examFindings: data.o_examFindings,
          ultrasoundDate: data.o_ultrasoundDate,
          ultrasound: data.o_ultrasound
        }));

      if (!_.isEmpty(data.o_physicalExam) ||
        data.o_consciousnessLevel ||
        data.o_painAssessment)
        promises.push(this.createPhysicalExam({
          ...data,
          physicalExam: data.o_physicalExam,
          consciousnessLevel: data.o_consciousnessLevel,
          painAssessment: data.o_painAssessment
        }))

      if (data.attachmentURLs || data.pendingAttachmentURLs)
        promises.push(this.createAttachments({
          ...data,
          attachmentURLs: data.attachmentURLs,
          pendingAttachmentURLs: data.pendingAttachmentURLs
        }));

      if (!_.isEmpty(data.a_impression))
        promises.push(this.createImpression({
          ...data,
          text: data.a_impression
        }))

      if (!_.isEmpty(data.a_diagnostic) ||
        !_.isEmpty(data.a_diagnosticICD10) ||
        (data.a_diagnosticProcedures && data.a_diagnosticProcedures.length > 0))
        promises.push(this.createDiagnosis({
          ...data,
          text: data.a_diagnostic,
          icd10: data.a_diagnosticICD10,
          recommendedProcedures: data.a_diagnosticProcedures
        }))

      if (!_.isEmpty(data.p_carePlanNotes))
        promises.push(this.createCarePlan({
          ...data,
          text: data.p_carePlanNotes
        }))
    }

    return promises.length > 0 ? Promise.all(promises) : Promise.resolve({});
  }

  createChiefComplaint(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'text']),
      type: 'chief-complaint'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createHPI(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'text']),
      type: 'hpi'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createVital(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'height', 'weight', 'pulse', 'respiration', 'oxygenSaturation', 'temperature', 'temperatureMethod',
        'bpDiastolic', 'bpSystolic', 'headCircumference', 'waistCircumference', 'neckCircumference',
        'abdominalCircumference']),
      type: 'vitals'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createENTNote(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'cpapPressure', 'lsatPressure', 'bipapInspiration', 'bipapExpiration',
        'ahi', 'rampTime', 'psgDiagnostic', 'psgTherapeutic', 'psgSplit',]),
      type: 'ent-note'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createOBNote(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'lmp', 'fundalHeight', 'fetalHeartTone', 'examFindings', 'ultrasoundDate',
        'ultrasoundAog']),
      type: 'ob-note'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createPhysicalExam(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'consciousnessLevel', 'painAssessment', 'physicalExam']),
      type: 'physical-exam'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createAttachments(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'attachmentURLs']),
      type: 'attachment'
    };

    return this.sdk.medicalRecords()
      .create(newData)
      .then(attachment => this.uploadPendingAttachments(attachment.id, data.pendingAttachmentURLs)
        .then(urls => urls.length > 0 ? this.sdk.medicalRecords()
          .update(attachment.id, { $addToSet: { attachmentURLs: { $each: urls } } })
          .then(updatedAttachment =>
            ({ ...attachment, attachmentURLs: attachment.attachmentURLs ? attachment.attachmentURLs.concat(urls) : urls })) :
          Promise.resolve(attachment)));
  }

  private uploadPendingAttachments(recordId: string, pendingUploads: any[]): Promise<string[]> {
    return pendingUploads && pendingUploads.length > 0 ?
      Promise.all(pendingUploads.map(attachment => this.sdk.medicalRecords()
        .uploadPic(recordId, attachment.url).upload()
        //.then((task: any) => task.upload())
        .then((url: any) => url))) :
      Promise.resolve([]);
  }

  createImpression(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'text']),
      type: 'assessment',
      subtype: 'impression'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createDiagnosis(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'text', 'icd10', 'recommendedProcedures']),
      type: 'assessment',
      subtype: 'diagnosis'
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  createCarePlan(data: any): Promise<any> {
    const newData: any = {
      ..._.pick(data, ['patient', 'facility', 'encounter', 'account',
        'text']),
      type: 'care-plan',
    };

    return this.sdk.medicalRecords()
      .create(newData);
  }

  loadMedicalRecordsToProgressNote(patient: string, encounter: string): Promise<any> {
    return this.sdk.medicalRecords()
      .find({
        encounter, patient,
        type: {
          $in: ['chief-complaint', 'hpi', 'vitals', 'ob-note',
            'ent-note', 'physical-exam', 'care-plan', 'assessment', 'attachment']
        }
      })
      .then(res => res.items)
      .then(medRecords => this.mapMedicalRecordsToProgressNote(patient, encounter, medRecords));
  }



  private mapMedicalRecordsToProgressNote(patient: string, encounter: string, medicalRecords: any[]): any {
    if (medicalRecords && medicalRecords.length > 0) {
      let progressNote: any = { patient, encounter, type: 'progress-note' };
      for (let medRecord of medicalRecords) {
        progressNote.facility = medRecord.facility ? medRecord.facility : progressNote.facility;
        if (medRecord.type === "chief-complaint") {
          progressNote = { ...progressNote, s_cc: medRecord.text };
        } else if (medRecord.type === "hpi") {
          progressNote = { ...progressNote, s_hpi: medRecord.text };
        } else if (medRecord.type === "vitals") {
          progressNote = {
            ...progressNote,
            o_weight: medRecord.weight,
            o_pulse: medRecord.pulse,
            o_respiration: medRecord.respiration,
            o_oxygenSaturation: medRecord.oxygenSaturation,
            o_temperature: medRecord.temperature,
            o_temperatureMethod: medRecord.temperatureMethod,
            o_bpDiastolic: medRecord.bpDiastolic,
            o_bpSystolic: medRecord.bpSystolic,
            o_headCircumference: medRecord.headCircumference,
            o_waistCircumference: medRecord.waistCircumference,
            o_neckCircumference: medRecord.neckCircumference,
            o_abdominalCircumference: medRecord.abdominalCircumference,
          };
        } else if (medRecord.type === "ob-note") {
          progressNote = {
            ...progressNote,
            o_fundalHeight: medRecord.fundalHeight,
            o_fetalHeartTone: medRecord.fetalHeartTone,
            o_examFindings: medRecord.examFindings,
            o_ultrasoundDate: medRecord.ultrasoundDate,
            o_ultrasoundAog: medRecord.ultrasoundAog
          };
        } else if (medRecord.type === "ent-note") {
          progressNote = {
            ...progressNote,
            o_cpapPressure: medRecord.cpapPressure,
            o_bipapInspiration: medRecord.bipapInspiration,
            o_bipapExpiration: medRecord.bipapExpiration,
            o_lsatPressure: medRecord.lsatPressure,
            o_ahi: medRecord.ahi,
            o_rampTime: medRecord.rampTime,
            o_psgDiagnostic: medRecord.psgDiagnostic,
            o_psgTherapeutic: medRecord.psgTherapeutic,
            o_psgSplit: medRecord.psgSplit
          };
        } else if (medRecord.type === "physical-exam") {
          progressNote = {
            ...progressNote,
            o_physicalExam: medRecord.physicalExam,
            o_consciousnessLevel: medRecord.consciousnessLevel,
            o_painAssessment: medRecord.painAssessment,
          };
        } else if (medRecord.type === "attachment") {
          progressNote = { ...progressNote, attachmentURLs: medRecord.attachmentURLs };
        } else if (medRecord.type === "assessment" && medRecord.subtype === "impression") {
          progressNote = { ...progressNote, a_impression: medRecord.text };
        } else if (medRecord.type === "assessment" && medRecord.subtype === "diagnosis") {
          progressNote = {
            ...progressNote,
            a_diagnostic: medRecord.text,
            a_diagnosticICD10: medRecord.icd10,
            a_diagnosticProcedures: medRecord.recommendedProcedures
          };
        } else if (medRecord.type === "care-plan") {
          progressNote = { ...progressNote, p_carePlanNotes: medRecord.text };
        }
      }
      return progressNote;
    }

    return undefined;
  }
}
